import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { AudioStreamPlayer } from '../utils/AudioStreamPlayer';
import { getAPIURL } from '../api/api';

const SocketContext = createContext(null);
const TRUSTED_ACCESS_STORAGE_KEY = 'trustedChildAccessApprovals';

const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [childStatuses, setChildStatuses] = useState({});
  const [liveLocation, setLiveLocation] = useState({});
  const [sosAlerts, setSosAlerts] = useState([]);
  const [riskAlerts, setRiskAlerts] = useState([]);
  const [accessResponses, setAccessResponses] = useState([]);
  const [latestVideoFrame, setLatestVideoFrame] = useState(null);
  const [latestScreenFrame, setLatestScreenFrame] = useState(null);
  const [pendingAccessRequest, setPendingAccessRequest] = useState(null);
  const [activeIncomingMedia, setActiveIncomingMedia] = useState(null);

  // WebRTC peer connections & frame streaming
  const peerConnectionsRef = useRef({});
  const onStreamFrameRef = useRef(null); // Callback for receiving video frames

  // Child-web fallback media streaming refs
  const outgoingMediaStreamRef = useRef(null);
  const outgoingFrameIntervalRef = useRef(null);
  const outgoingVideoElementRef = useRef(null);
  const outgoingCanvasRef = useRef(null);
  const outgoingAudioContextRef = useRef(null);
  const outgoingAudioSourceRef = useRef(null);
  const outgoingAudioProcessorRef = useRef(null);
  const wakeLockRef = useRef(null);
  const outgoingRafRef = useRef(null);
  const lastFrameSentAtRef = useRef(0);
  const frameInFlightRef = useRef(false);

  const getTrustedApprovals = () => {
    try {
      const raw = localStorage.getItem(TRUSTED_ACCESS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      console.warn('Failed to parse trusted approvals:', error);
      return {};
    }
  };

  const setTrustedApproval = (parentId, type, approved) => {
    const key = `${parentId}:${type}`;
    const store = getTrustedApprovals();
    if (approved) {
      store[key] = true;
    } else {
      delete store[key];
    }
    localStorage.setItem(TRUSTED_ACCESS_STORAGE_KEY, JSON.stringify(store));
  };

  const stopOutgoingMediaStream = () => {
    if (outgoingRafRef.current) {
      cancelAnimationFrame(outgoingRafRef.current);
      outgoingRafRef.current = null;
    }

    if (outgoingFrameIntervalRef.current) {
      clearInterval(outgoingFrameIntervalRef.current);
      outgoingFrameIntervalRef.current = null;
    }

    frameInFlightRef.current = false;
    lastFrameSentAtRef.current = 0;

    if (outgoingAudioProcessorRef.current) {
      outgoingAudioProcessorRef.current.onaudioprocess = null;
      outgoingAudioProcessorRef.current.disconnect();
      outgoingAudioProcessorRef.current = null;
    }

    if (outgoingAudioSourceRef.current) {
      outgoingAudioSourceRef.current.disconnect();
      outgoingAudioSourceRef.current = null;
    }

    if (outgoingAudioContextRef.current) {
      outgoingAudioContextRef.current.close().catch(() => {});
      outgoingAudioContextRef.current = null;
    }

    if (outgoingMediaStreamRef.current) {
      outgoingMediaStreamRef.current.getTracks().forEach((track) => track.stop());
      outgoingMediaStreamRef.current = null;
    }

    outgoingVideoElementRef.current = null;
    outgoingCanvasRef.current = null;
    setActiveIncomingMedia(null);

    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  };

  const requestWakeLock = async () => {
    try {
      if (!('wakeLock' in navigator)) return;
      if (wakeLockRef.current) return;
      const lock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = lock;
      lock.addEventListener('release', () => {
        wakeLockRef.current = null;
      });
    } catch (error) {
      console.warn('Wake lock unavailable:', error);
    }
  };

  const startFrameStreaming = (stream, parentId, eventName) => {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { alpha: false });
    outgoingVideoElementRef.current = video;
    outgoingCanvasRef.current = canvas;

    const maxDimension = 480;
    const minFrameGapMs = 130;

    const sendFrame = (timestamp) => {
      if (!socketRef.current?.connected || video.readyState < 2 || !context) {
        outgoingRafRef.current = requestAnimationFrame(sendFrame);
        return;
      }

      if (timestamp - lastFrameSentAtRef.current < minFrameGapMs || frameInFlightRef.current) {
        outgoingRafRef.current = requestAnimationFrame(sendFrame);
        return;
      }

      const sourceWidth = video.videoWidth || 640;
      const sourceHeight = video.videoHeight || 360;
      const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
      const width = Math.max(2, Math.floor(sourceWidth * scale));
      const height = Math.max(2, Math.floor(sourceHeight * scale));

      canvas.width = width;
      canvas.height = height;
      context.drawImage(video, 0, 0, width, height);

      frameInFlightRef.current = true;
      const frame = canvas.toDataURL('image/jpeg', 0.5).split(',')[1];
      socketRef.current.emit(eventName, { parentId, image: frame });
      lastFrameSentAtRef.current = timestamp;
      frameInFlightRef.current = false;

      outgoingRafRef.current = requestAnimationFrame(sendFrame);
    };

    video.play().catch(() => {});
    outgoingRafRef.current = requestAnimationFrame(sendFrame);
  };

  const startCameraStreamForParent = async (parentId, facing = 'front') => {
    const constraints = {
      video: {
        facingMode: facing === 'back' ? { ideal: 'environment' } : 'user',
        width: { ideal: 640 },
        height: { ideal: 360 }
      },
      audio: false
    };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    await requestWakeLock();
    outgoingMediaStreamRef.current = stream;
    setActiveIncomingMedia({ type: 'camera', parentId, startedAt: Date.now() });
    startFrameStreaming(stream, parentId, 'video_frame');
  };

  const startScreenStreamForParent = async (parentId) => {
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    await requestWakeLock();
    outgoingMediaStreamRef.current = stream;
    setActiveIncomingMedia({ type: 'screen_share', parentId, startedAt: Date.now() });

    socketRef.current?.emit('screen_share_status', {
      parentId,
      status: 'started'
    });

    stream.getVideoTracks().forEach((track) => {
      track.onended = () => {
        socketRef.current?.emit('screen_share_status', {
          parentId,
          status: 'stopped'
        });
        stopOutgoingMediaStream();
      };
    });

    startFrameStreaming(stream, parentId, 'screen_frame');
  };

  const startMicrophoneStreamForParent = async (parentId) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: { ideal: 48000 },
        channelCount: { ideal: 1 },
        latency: { ideal: 0.02 }
      },
      video: false
    });

    await requestWakeLock();
    outgoingMediaStreamRef.current = stream;
    setActiveIncomingMedia({ type: 'microphone', parentId, startedAt: Date.now() });

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContextClass({ sampleRate: 16000 });
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(2048, 1, 1);

    outgoingAudioContextRef.current = audioContext;
    outgoingAudioSourceRef.current = source;
    outgoingAudioProcessorRef.current = processor;

    processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      const pcmBuffer = new ArrayBuffer(input.length * 2);
      const view = new DataView(pcmBuffer);

      for (let i = 0; i < input.length; i++) {
        const sample = Math.max(-1, Math.min(1, input[i]));
        view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      }

      if (socketRef.current?.connected) {
        socketRef.current.emit('audio_frame', {
          parentId,
          audio: arrayBufferToBase64(pcmBuffer)
        });
      }
    };

    source.connect(processor);
    processor.connect(audioContext.destination);
  };

  const respondToAccessRequest = ({ approved, remember }) => {
    const request = pendingAccessRequest;
    if (!request || !socketRef.current?.connected) return;

    socketRef.current.emit('access_response', {
      parentId: request.parentId,
      type: request.type,
      approved
    });

    if (remember) {
      setTrustedApproval(request.parentId, request.type, approved);
    }

    setPendingAccessRequest(null);
    if (!approved) {
      stopOutgoingMediaStream();
    }
  };

  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setPendingAccessRequest(null);
      stopOutgoingMediaStream();
      setIsConnected(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setIsConnected(false);
      return;
    }
    const fallbackSocketUrl = (() => {
      try {
        return new URL(getAPIURL()).origin;
      } catch (e) {
        return 'https://atry-ungravitational-claude.ngrok-free.dev';
      }
    })();
    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || fallbackSocketUrl;

    const socket = io(SOCKET_URL, {
      auth: { token },
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      transports: ['websocket', 'polling'],
      upgradeTimeout: 10000,
      reconnection: true,
      secure: false,
      rejectUnauthorized: false,
      forceNew: false,
      multiplex: true
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('🔌 Socket connected successfully on transport:', socket.io.engine.transport.name);
      setIsConnected(true);
    });

    socket.on('disconnect', (reason) => {
      console.warn('⚠️ Socket disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
    });

    socket.on('error', (error) => {
      console.error('❌ Socket error:', error);
    });

    // Child online/offline status
    socket.on('child_status', (data) => {
      setChildStatuses(prev => ({
        ...prev,
        [data.childId]: { isOnline: data.isOnline, timestamp: data.timestamp }
      }));
    });

    // Live location updates
    socket.on('location_update', (data) => {
      setLiveLocation(prev => ({
        ...prev,
        [data.childId]: data
      }));
    });

    // SOS alerts
    socket.on('sos_alert', (alert) => {
      setSosAlerts(prev => [alert, ...prev]);
    });

    // Risk behavior alerts
    socket.on('risk_alerts', (data) => {
      setRiskAlerts(prev => [data, ...prev.slice(0, 19)]);
    });

    // Access request responses from child
    socket.on('access_response', (data) => {
      setAccessResponses(prev => [data, ...prev.slice(0, 9)]);
    });

    // Parent -> child request events for web child fallback.
    socket.on('access_request', (data) => {
      if (user?.role !== 'child') return;
      if (!data?.parentId || !data?.type) return;

      const trusted = Boolean(getTrustedApprovals()[`${data.parentId}:${data.type}`]);
      if (trusted) {
        socket.emit('access_response', {
          parentId: data.parentId,
          type: data.type,
          approved: true
        });
        return;
      }

      setPendingAccessRequest(data);
    });

    socket.on('webrtc_offer', async (data) => {
      if (user?.role !== 'child') return;

      const { parentId, mediaType, facing } = data || {};
      if (!parentId || !mediaType) return;

      try {
        stopOutgoingMediaStream();

        if (mediaType === 'camera') {
          await startCameraStreamForParent(parentId, facing);
        } else if (mediaType === 'microphone') {
          await startMicrophoneStreamForParent(parentId);
        } else if (mediaType === 'screen_share') {
          await startScreenStreamForParent(parentId);
        } else {
          return;
        }

        socket.emit('webrtc_answer', {
          parentId,
          answer: { status: 'streaming_started', mediaType }
        });
      } catch (error) {
        console.error(`Failed to start ${mediaType} stream on web child:`, error);
        socket.emit('access_response', {
          parentId,
          type: mediaType,
          approved: false
        });
      }
    });

    // Video frame streaming from child
    socket.on('video_frame', (data) => {
      if (peerConnectionsRef.current['mediaType'] !== 'camera') return;
      if (data.image && onStreamFrameRef.current) {
        try {
          const imageUrl = `data:image/jpeg;base64,${data.image}`;
          setLatestVideoFrame(imageUrl);
          onStreamFrameRef.current(imageUrl);
        } catch (e) {
          console.error('Error processing video frame:', e);
        }
      }
    });

    // Audio frame streaming from child
    socket.on('audio_frame', (data) => {
      if (peerConnectionsRef.current['mediaType'] !== 'microphone') return;
      if (data.audio && onStreamFrameRef.current) {
        try {
          // Decode base64 to binary
          const binaryString = atob(data.audio);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Convert PCM16 to float32 samples
          const dataView = new DataView(bytes.buffer);
          const samples = new Float32Array(bytes.length / 2);
          for (let i = 0; i < samples.length; i++) {
            samples[i] = dataView.getInt16(i * 2, true) / 32768.0;
          }
          
          // Play audio through Web Audio API
          const audioPlayer = peerConnectionsRef.current['audioPlayer'];
          if (audioPlayer) {
            audioPlayer.play(samples);
          }
          
          // Also call the callback for custom handling
          onStreamFrameRef.current(samples);
        } catch (e) {
          console.error('Error processing audio frame:', e);
        }
      }
    });

    // Screen share status
    socket.on('screen_share_status', (data) => {
      console.log('🖥️ Screen share status:', data.status);
      // Could update state to show screen sharing status
    });

    // Screen frames
    socket.on('screen_frame', (data) => {
      if (peerConnectionsRef.current['mediaType'] !== 'screen_share') return;

      if (data.image) {
        const imageUrl = `data:image/jpeg;base64,${data.image}`;
        setLatestScreenFrame(imageUrl);
        if (onStreamFrameRef.current) onStreamFrameRef.current(imageUrl);
      } else {
        console.log('🖥️ Screen frame message:', data.message);
      }
    });

    // WebRTC signaling (simplified for frame streaming)
    socket.on('webrtc_answer', async (data) => {
      // For video_frame streaming, we don't need full WebRTC
      // Just signal that streaming has started
      if (onStreamFrameRef.current) {
        console.log('Child confirmed streaming');
      }
    });

    socket.on('webrtc_ice_candidate', async (data) => {
      // Not used in frame streaming mode
    });

    socket.on('webrtc_end', () => {
      stopOutgoingMediaStream();
      endWebRTCCall();
    });

    return () => {
      onStreamFrameRef.current = null;
      setPendingAccessRequest(null);
      stopOutgoingMediaStream();
      socket.disconnect();
      setIsConnected(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // WebRTC: Initiate call to child (Frame-based streaming)
  const startWebRTCCall = async (childId, mediaType, onStream, options = {}) => {
    // End any previous call to avoid stale state and one-time mic failures.
    if (peerConnectionsRef.current['active']) {
      endWebRTCCall();
    }

    // Initialize audio player if this is audio streaming
    if (mediaType === 'microphone') {
      const player = new AudioStreamPlayer();
      player.initialize();
      peerConnectionsRef.current['audioPlayer'] = player;
    }

    // Set callback to receive video frames or audio samples from child
    onStreamFrameRef.current = onStream;

    // Store call info
    peerConnectionsRef.current['active'] = true;
    peerConnectionsRef.current['childId'] = childId;
    peerConnectionsRef.current['mediaType'] = mediaType;

    if (mediaType === 'camera') setLatestVideoFrame(null);
    if (mediaType === 'screen_share') setLatestScreenFrame(null);

    // Send offer to child (child will respond with video_frame or audio_frame events)
    if (socketRef.current) {
      socketRef.current.emit('webrtc_offer', {
        childId,
        offer: {},
        mediaType,
        ...(mediaType === 'camera' ? { facing: options.facing || 'front' } : {})
      });
    }
    return true;
  };

  const endWebRTCCall = (duration) => {
    const childId = peerConnectionsRef.current['childId'];
    const mediaType = peerConnectionsRef.current['mediaType'];

    // Cleanup audio player if present
    const audioPlayer = peerConnectionsRef.current['audioPlayer'];
    if (audioPlayer) {
      audioPlayer.stop();
    }

    // Clear frame callback
    onStreamFrameRef.current = null;

    if (socketRef.current && socketRef.current.connected && childId) {
      socketRef.current.emit('webrtc_end', { targetId: childId, mediaType, duration });
    }

    peerConnectionsRef.current = {};
  };

  const emitToChild = (event, data) => {
    if (socketRef.current) socketRef.current.emit(event, data);
  };

  const clearSosAlert = (alertId) => {
    setSosAlerts(prev => prev.filter(a => a.alertId !== alertId));
  };

  return (
    <SocketContext.Provider value={{
      socket: socketRef.current,
      isConnected,
      childStatuses,
      liveLocation,
      sosAlerts,
      riskAlerts,
      accessResponses,
      latestVideoFrame,
      latestScreenFrame,
      pendingAccessRequest,
      activeIncomingMedia,
      respondToAccessRequest,
      startWebRTCCall,
      endWebRTCCall,
      emitToChild,
      clearSosAlert,
      setSosAlerts,
      setRiskAlerts,
    }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
